import React, { useState, useEffect, useCallback } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { testimonials } from '../../data/mock';
import useScrollAnimation from '../../hooks/useScrollAnimation';

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const { ref: sectionRef, isVisible } = useScrollAnimation();

  // Minimum swipe distance
  const minSwipeDistance = 50;

  const goToNext = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    setTimeout(() => setIsAnimating(false), 500);
  }, [isAnimating]);

  const goToPrev = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setTimeout(() => setIsAnimating(false), 500);
  }, [isAnimating]);

  // Touch handlers for swipe
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrev();
    }
  };

  // Auto slide for mobile
  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    if (!isMobile) return;

    const interval = setInterval(() => {
      goToNext();
    }, 5000);

    return () => clearInterval(interval);
  }, [goToNext]);

  const TestimonialCard = ({ testimonial, className = '' }) => (
    <div className={`group relative bg-gradient-to-br from-[#1F1914] to-[#1A1512] rounded-2xl p-6 border border-[#C9A97B]/10 hover:border-[#C9A97B]/30 transition-all duration-500 hover-lift ${className}`}>
      {/* Quote Icon */}
      <div className="absolute top-6 right-6 opacity-10 group-hover:opacity-20 transition-opacity">
        <Quote className="w-12 h-12 text-[#C9A97B]" />
      </div>

      {/* Rating */}
      <div className="flex gap-1 mb-4">
        {[...Array(testimonial.rating)].map((_, i) => (
          <Star key={i} className="w-4 h-4 text-[#C9A97B] fill-[#C9A97B]" />
        ))}
      </div>

      {/* Quote */}
      <p className="text-[#BFB5A5] text-base leading-relaxed mb-6 relative z-10">
        "{testimonial.quote}"
      </p>

      {/* Author */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#C9A97B] to-[#A68B5B] flex items-center justify-center">
          <span className="text-[#1A1512] font-display font-bold text-lg">
            {testimonial.name.charAt(0)}
          </span>
        </div>
        <div>
          <div className="text-[#F5F0E8] font-medium">{testimonial.name}</div>
          <div className="text-[#7A7067] text-sm">{testimonial.title}</div>
          <div className="text-[#C9A97B] text-xs">{testimonial.location}</div>
        </div>
      </div>
    </div>
  );

  return (
    <section ref={sectionRef} className="py-24 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-[#0D0B09]" />
      
      {/* Decorative */}
      <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-[#C9A97B]/5 rounded-full blur-[150px]" />
      <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#472F27]/20 rounded-full blur-[150px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className={`text-center mb-16 ${isVisible ? 'animate-fadeInDown' : 'opacity-0'}`}>
          <span className={`inline-block px-4 py-1.5 rounded-full bg-[#C9A97B]/10 text-[#C9A97B] text-sm font-medium mb-4 ${isVisible ? 'animate-bounceIn' : 'opacity-0'}`} style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}>
            Müşteri Yorumları
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-[#F5F0E8] mb-4">
            Kuyumcular <span className="text-gold-gradient">Ne Diyor</span>?
          </h2>
          <p className="text-[#BFB5A5] text-lg max-w-2xl mx-auto">
            Türkiye'nin dört bir yanından kuyumcular Tam Ayar ile tanıştı.
          </p>
        </div>

        {/* Desktop Grid - Hidden on Mobile */}
        <div className="hidden md:grid md:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => {
            // Sol: soldan, orta: alttan scale, sağ: sağdan
            const animationClass = index === 0 
              ? 'animate-slideInLeft' 
              : index === 1 
                ? 'animate-scaleIn' 
                : 'animate-slideInRight';
            return (
              <div 
                key={testimonial.id}
                className={isVisible ? animationClass : 'opacity-0'}
                style={{ animationDelay: `${index * 200 + 350}ms`, animationFillMode: 'forwards' }}
              >
                <TestimonialCard testimonial={testimonial} />
              </div>
            );
          })}
        </div>

        {/* Mobile Slider */}
        <div className="md:hidden">
          <div 
            className="relative overflow-hidden"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <div 
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} className="w-full flex-shrink-0 px-1">
                  <TestimonialCard testimonial={testimonial} />
                </div>
              ))}
            </div>
          </div>

          {/* Mobile Navigation */}
          <div className="flex items-center justify-center gap-4 mt-6">
            {/* Prev Button */}
            <button
              onClick={goToPrev}
              disabled={isAnimating}
              className="w-10 h-10 rounded-full bg-[#1F1914] border border-[#C9A97B]/20 flex items-center justify-center text-[#C9A97B] hover:bg-[#C9A97B]/10 transition-colors disabled:opacity-50"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            {/* Dots */}
            <div className="flex gap-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    if (!isAnimating) {
                      setIsAnimating(true);
                      setCurrentIndex(index);
                      setTimeout(() => setIsAnimating(false), 500);
                    }
                  }}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === currentIndex 
                      ? 'w-8 bg-[#C9A97B]' 
                      : 'w-2 bg-[#C9A97B]/30 hover:bg-[#C9A97B]/50'
                  }`}
                />
              ))}
            </div>

            {/* Next Button */}
            <button
              onClick={goToNext}
              disabled={isAnimating}
              className="w-10 h-10 rounded-full bg-[#1F1914] border border-[#C9A97B]/20 flex items-center justify-center text-[#C9A97B] hover:bg-[#C9A97B]/10 transition-colors disabled:opacity-50"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
